import type { MissionAction } from '../../api';
import type { DiaryComposeIntent } from '../../components/DiaryFeed';
import type { ExamLaunchOptions } from '../../components/IntellectCenter';
import type { WellnessWorkspace } from '../../components/WellnessCenter';
import { useAppNavigation } from '../../navigation/AppNavigation';
import type { ViewMode } from '../../navigation/routes';

const composeTypes: DiaryComposeIntent[] = [
  'grudge',
  'gratitude',
  'note',
  'mood',
  'fitness',
  'tech',
  'categories',
  'public-note',
];
export function usePageState() {
  const navigation = useAppNavigation();
  const { route, params = {} } = navigation.current;
  const viewMode = (['person', 'profile', 'users'].includes(route) ? 'diary' : route) as ViewMode;
  const selectedPersonId = route === 'person' ? Number(params.personId) || null : null;
  const showProfile = route === 'profile';
  const showUserMgmt = route === 'users';
  const setViewMode = (next: ViewMode) => navigation.navigate(next);
  const setSelectedPersonId = (personId: number | null) => {
    if (personId) navigation.navigate('person', { personId });
    else if (route === 'person') navigation.back();
  };
  const setShowProfile = (show: boolean) =>
    show ? navigation.navigate('profile') : navigation.back();
  const setShowUserMgmt = (show: boolean) =>
    show ? navigation.navigate('users') : navigation.back();
  const nutritionEntryIntent = ['meal', 'water', 'review'].includes(String(params.intent))
    ? (params.intent as 'meal' | 'water' | 'review')
    : undefined;
  const nutritionInitialDate =
    typeof params.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(params.date)
      ? params.date
      : undefined;
  const recordsInitialFilter = params.filter === 'note' ? ('note' as const) : ('all' as const);
  const diaryComposeIntent = composeTypes.includes(params.compose as DiaryComposeIntent)
    ? (params.compose as DiaryComposeIntent)
    : null;
  const healthInitialTab: 'overview' | 'metrics' | 'reports' =
    params.tab === 'metrics' || params.tab === 'reports' ? params.tab : 'overview';
  const examLaunch: ExamLaunchOptions = {
    category: typeof params.category === 'string' ? params.category : undefined,
    pathwayId: typeof params.pathwayId === 'string' ? params.pathwayId : undefined,
  };
  const openNutrition = (intent?: 'meal' | 'water' | 'review', date?: string) =>
    navigation.navigate('nutrition', { intent, date });
  const openHealth = (tab: 'overview' | 'metrics' | 'reports' = 'overview') =>
    navigation.navigate('health', { tab });
  const openRecords = (filter: 'all' | 'note' = 'all', compose?: DiaryComposeIntent) =>
    navigation.navigate('records', { filter, compose });
  const openDiaryCompose = (compose: DiaryComposeIntent) => openRecords('all', compose);
  const consumeDiaryCompose = () =>
    navigation.replace('records', { ...params, compose: undefined });
  const closeBodyWorkspaceChild = () => {
    navigation.back();
  };
  const navigateWellnessWorkspace = (workspace: WellnessWorkspace) => {
    if (workspace === 'overview') navigation.navigate('wellness');
    else if (workspace === 'metrics' || workspace === 'reports') openHealth(workspace);
    else if (workspace === 'nutrition') openNutrition();
    else if (workspace === 'fitness') navigation.navigate('fitness');
  };
  const openMissionAction = (action: MissionAction) => {
    if (action.type === 'meal') openNutrition('meal');
    else if (action.type === 'exercise') navigation.navigate('fitness', { record: true });
    else if (action.type === 'note') openDiaryCompose('note');
    else if (action.type === 'study')
      navigation.navigate('pathway', { pathwayId: action.pathwayId, section: 'growth' });
    else navigation.navigate('missions');
  };
  const openExam = (options: ExamLaunchOptions = {}) => navigation.navigate('exam', { ...options });
  return {
    navigation,
    viewMode,
    selectedPersonId,
    showProfile,
    showUserMgmt,
    setViewMode,
    setSelectedPersonId,
    setShowProfile,
    setShowUserMgmt,
    nutritionEntryIntent,
    nutritionInitialDate,
    recordsInitialFilter,
    diaryComposeIntent,
    examLaunch,
    healthInitialTab,
    closeBodyWorkspaceChild,
    navigateWellnessWorkspace,
    openDiaryCompose,
    consumeDiaryCompose,
    openMissionAction,
    openExam,
    openNutrition,
    openHealth,
    openRecords,
  };
}
